import { ComponentProps, useCallback, useMemo } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { Content } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { useGetMetricsInUse } from 'src/hooks/useGetMetricsInUse';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { ContentId } from 'src/utils/constants/contentId';
import { Tag } from 'src/WebApiController';

import { EditTagMetadataFormProps } from '../TagsFormBody.types';
import { EditTagMetadataForm } from './EditTagMetadataForm';

export type EditTagMetadataDialogProps = {
  tagsWithValue: Tag[];
  tagMetadataToEdit: Tag;
  onTagChange: (tagMetadata: Tag, newValues: Tag[]) => void;
} & Pick<EditTagMetadataFormProps, 'entityType' | 'onClose'> &
  ComponentProps<typeof RSModal>;

export function EditTagMetadataDialog({
  tagMetadataToEdit,
  tagsWithValue,
  onClose,
  onTagChange,
  entityType,
  ...rest
}: EditTagMetadataDialogProps) {
  const {
    tagsMetadata: existingTagsCurrentType = [],
    tagsWithNoValue: existingTagsCurrentTypeWithNoValue = [],
    mergeEntityTagsMetadata,
    deleteEntityTagsMetadata,
  } = useTagsForEntityType(entityType);
  const { metricsInUse } = useGetMetricsInUse({ entityType });

  // Merge tags with no value from the entity type with the ones from the entity
  const existingTagsCurrentTypeWithNoValueMerged = useMemo(
    () =>
      existingTagsCurrentTypeWithNoValue.filter(
        (t) => tagsWithValue.find((t2) => t2.key === t.key) == null
      ),
    [existingTagsCurrentTypeWithNoValue, tagsWithValue]
  );

  const onTagMetadataUpdate = useCallback(
    async (tagMetadataUpdate: Tag) => {
      // Update tags metadata immediately
      await mergeEntityTagsMetadata([tagMetadataUpdate]);
    },
    [mergeEntityTagsMetadata]
  );

  const onTagMetadataDelete = useCallback(
    async (tag: Tag) => {
      // Delete tags metadata immediately
      await deleteEntityTagsMetadata([tag]);
      // Remove tags with the deleted key in the entity in edit mode
      onTagChange(tag, []);
    },
    [deleteEntityTagsMetadata, onTagChange]
  );

  return (
    <GenericDialog
      header={<Content id={ContentId.EditTagTypes} />}
      footer={<></>}
      {...rest}
      size={'lg'}
      onCancel={onClose}
    >
      <EditTagMetadataForm
        tagMetadataToEdit={tagMetadataToEdit}
        mergedTagsMetadata={existingTagsCurrentType ?? []}
        mergedTagsMetadataWithNoValue={existingTagsCurrentTypeWithNoValueMerged}
        onSubmit={onTagMetadataUpdate}
        onDeleteSubmit={onTagMetadataDelete}
        onClose={onClose}
        metricsInUse={metricsInUse}
        entityType={entityType}
        useDialogStyle
      />
    </GenericDialog>
  );
}
